import { Link } from "react-router-dom";
import { useState } from "react";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../components/CartContext";
import api from "../lib/api";

export default function Cart() {
  const { items, remove, updateQty, total, count } = useCart();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");

  const checkout = async () => {
    if (!items.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: email || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-cart" className="container-fld py-20">
      <div className="overline mb-4">// Cart</div>
      <h1 className="font-tactical text-4xl md:text-6xl font-bold uppercase text-white tracking-tight leading-none">
        Mission <span className="text-[#7CFF00]">Loadout.</span>
      </h1>

      {count === 0 ? (
        <div className="mt-16 tactical-card p-16 text-center" data-testid="cart-empty">
          <ShoppingBag className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
          <p className="font-tactical text-2xl uppercase text-white tracking-wider">
            Your loadout is empty
          </p>
          <p className="text-zinc-500 mt-3">Browse the merch store to gear up.</p>
          <Link to="/merch" className="btn-primary mt-8" data-testid="cart-shop-cta">
            Shop Merch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-3" data-testid="cart-items">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`cart-item-${item.id}`}
                className="tactical-card p-4 flex gap-4 items-center"
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 overflow-hidden bg-[#0A0A0A]">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline text-[10px]">{item.category}</div>
                  <div className="font-tactical text-white uppercase tracking-wider mt-1 truncate">
                    {item.name}
                  </div>
                  <div className="text-[#7CFF00] font-mono-tactical text-sm mt-1">
                    ${item.price.toFixed(2)}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <div className="flex items-center border border-white/15">
                    <button
                      onClick={() => updateQty(item.id, item.quantity - 1)}
                      data-testid={`cart-decr-${item.id}`}
                      className="px-2 py-1 text-zinc-400 hover:text-[#7CFF00]"
                      aria-label="Decrease"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-3 font-mono-tactical text-sm text-white">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQty(item.id, item.quantity + 1)}
                      data-testid={`cart-incr-${item.id}`}
                      className="px-2 py-1 text-zinc-400 hover:text-[#7CFF00]"
                      aria-label="Increase"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => remove(item.id)}
                    data-testid={`cart-remove-${item.id}`}
                    className="text-zinc-500 hover:text-red-500 transition-colors"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-4">
            <div className="tactical-card p-6 sticky top-24">
              <div className="overline mb-4">// Order Summary</div>
              <div className="flex justify-between text-zinc-400 text-sm">
                <span>Subtotal ({count} items)</span>
                <span className="text-white font-mono-tactical" data-testid="cart-subtotal">
                  ${total.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-zinc-400 text-sm mt-2">
                <span>Shipping</span>
                <span className="text-zinc-500">Calculated at checkout</span>
              </div>
              <div className="border-t border-white/10 mt-5 pt-5 flex justify-between">
                <span className="font-tactical uppercase text-white tracking-wider">
                  Total
                </span>
                <span
                  data-testid="cart-total"
                  className="font-tactical text-[#7CFF00] text-2xl"
                >
                  ${total.toFixed(2)}
                </span>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for receipt (optional)"
                data-testid="cart-email"
                className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 text-sm mt-5 text-white focus:border-[#7CFF00] focus:outline-none"
              />
              <button
                onClick={checkout}
                disabled={busy}
                data-testid="cart-checkout"
                className="btn-primary w-full mt-4 justify-center disabled:opacity-50"
              >
                {busy ? "Redirecting..." : "Secure Checkout"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
